import type { PreviewReview, ReviewSourceId } from '@flexbyt/shared';

/**
 * Illustrative reviews, per source, for the product demonstrations.
 *
 * ===========================================================================
 * What these are, and what they are not
 * ===========================================================================
 * These are **not** testimonials and are never presented as any. They are the
 * content inside a demonstration: when a visitor clicks "Tripadvisor" in the
 * hero, these are the reviews that flow through the library and into the
 * widget, so they have to sound like Tripadvisor reviews of a hotel rather than
 * like Google reviews of a dentist.
 *
 * ReviewsJet's own customer reviews are a completely separate thing and live in
 * `testimonials.ts`, where they come from the API and are never invented.
 *
 * Every review here carries `isSample: true`, the same flag the dashboard sets
 * on the sample set it shows a new account.
 *
 * ===========================================================================
 * Why not reuse SAMPLE_REVIEWS
 * ===========================================================================
 * The shared sample set is twenty reviews weighted towards Google, Trustpilot
 * and Etsy — one Yelp, one Tripadvisor, one Airbnb. A source picker that shows
 * three Google reviews and then a single lonely Airbnb one demonstrates nothing
 * except that the demo ran out of data.
 *
 * More importantly the audiences are different, and the brief is explicit that
 * they must not be flattened: a hotel manager and an Etsy seller do not
 * recognise themselves in the same review. So each source gets copy written for
 * the business that would actually be reading it.
 *
 * The avatars are the shared drawn SVGs — flat illustrations we own, nobody's
 * likeness, already served from `public/images/sample`.
 */

const avatar = (n: number) => `/images/sample/a${n}.svg`;

function review(
  id: string,
  source: ReviewSourceId,
  authorName: string,
  authorAvatar: string | null,
  rating: number,
  title: string,
  content: string,
  reviewDate: string,
  reply?: string
): PreviewReview {
  return {
    id,
    authorName,
    authorAvatar,
    rating,
    title,
    content,
    reviewDate,
    source,
    pictures: [],
    reply: reply ?? null,
    isSample: true,
  };
}

/**
 * Eight per source.
 *
 * Four was enough for a carousel and not enough for anything else: the builder
 * offers up to eight reviews, and filling a masonry wall from four meant the
 * same review appearing twice on screen — which reads as a bug in the widget
 * rather than as a short sample set, and is the first thing a visitor notices.
 *
 * Ratings are not all five. A wall of nothing but fives is not what anybody's
 * reviews look like, and a demo that only ever shows a perfect score hides how
 * a four-star card reads — which is the thing a cautious visitor is checking
 * for.
 */
export const DEMO_REVIEWS: Record<string, PreviewReview[]> = {
  google: [
    review(
      'demo-google-1',
      'google',
      'Marcus Bell',
      avatar(1),
      5,
      'Booked again the same week',
      'Called on a Tuesday about a boiler that had given up and someone was out the next morning. Explained what was wrong without any of the usual theatre, and the price was the price he quoted.',
      '2026-07-02',
      'Thanks Marcus — glad we could get to you quickly.'
    ),
    review(
      'demo-google-2',
      'google',
      'Priya Raman',
      avatar(2),
      5,
      'Genuinely the best in the area',
      'Third time back. Consistent every visit, which is more than I can say for the two places I tried before.',
      '2026-06-18'
    ),
    review(
      'demo-google-3',
      'google',
      'Tom Baptiste',
      avatar(3),
      4,
      'Very good, one small niggle',
      'No complaints about the work itself. Parking is a nightmare but that is hardly their fault.',
      '2026-06-04'
    ),
    review(
      'demo-google-4',
      'google',
      'Hannah Meyer',
      avatar(4),
      5,
      'Straightforward and honest',
      'Told me what it would cost, then did it for that. No upsell.',
      '2026-05-21'
    ),
    review(
      'demo-google-5',
      'google',
      'Ade Balogun',
      avatar(9),
      5,
      'Turned up when they said',
      'Which sounds like a low bar until you have waited in for three other firms. Clean, tidy, gone by eleven.',
      '2026-05-08'
    ),
    review(
      'demo-google-6',
      'google',
      'Sinead Doyle',
      avatar(10),
      5,
      'Sorted in one visit',
      'I had been told it would need two. It did not.',
      '2026-04-24'
    ),
    review(
      'demo-google-7',
      'google',
      'Peter Nowak',
      avatar(11),
      4,
      'Good work, slow to quote',
      'Took a few days to come back with a price. Once it did, everything was quick and the price held.',
      '2026-04-11'
    ),
    review(
      'demo-google-8',
      'google',
      'Grace Mensah',
      avatar(12),
      5,
      'Explained it properly',
      'Showed me what had failed and why, without making me feel stupid for not knowing.',
      '2026-03-29'
    ),
  ],

  trustpilot: [
    review(
      'demo-trustpilot-1',
      'trustpilot',
      'Daniel Brennan',
      avatar(5),
      5,
      'Support actually replied',
      'Had a billing question at eight in the evening and had a real answer before nine. That is rarer than it should be.',
      '2026-07-09'
    ),
    review(
      'demo-trustpilot-2',
      'trustpilot',
      'Elena Petrova',
      avatar(6),
      5,
      'Does exactly what it says',
      'Signed up expecting to spend an afternoon on setup and was finished before my coffee went cold.',
      '2026-06-27'
    ),
    review(
      'demo-trustpilot-3',
      'trustpilot',
      'Callum Hayes',
      avatar(7),
      4,
      'Solid, with room to grow',
      'Everything I needed is there. A couple of the reporting screens feel like they are still catching up with the rest.',
      '2026-06-11'
    ),
    review(
      'demo-trustpilot-4',
      'trustpilot',
      'Ravi Patel',
      avatar(8),
      5,
      'Cancelled the other subscription',
      'It replaced two tools we were paying for separately and does both jobs better.',
      '2026-05-30'
    ),
    review(
      'demo-trustpilot-5',
      'trustpilot',
      'Marek Kowalski',
      avatar(13),
      5,
      'Migration was painless',
      'Moved everything over on a Sunday afternoon expecting a mess. Nothing broke.',
      '2026-05-17'
    ),
    review(
      'demo-trustpilot-6',
      'trustpilot',
      'Aisha Rahman',
      avatar(14),
      5,
      'Worth it for the reporting alone',
      'I stopped keeping the spreadsheet I had been maintaining for two years.',
      '2026-05-03'
    ),
    review(
      'demo-trustpilot-7',
      'trustpilot',
      'Tomás Ferreira',
      avatar(15),
      4,
      'Very good, one gap',
      'Everything I use daily is excellent. I would like a proper export, and I am told it is coming.',
      '2026-04-19'
    ),
    review(
      'demo-trustpilot-8',
      'trustpilot',
      'Freya Lindqvist',
      avatar(16),
      5,
      'The onboarding actually helped',
      'Half an hour with someone who knew the product, and we were running properly rather than approximately.',
      '2026-04-02'
    ),
  ],

  yelp: [
    review(
      'demo-yelp-1',
      'yelp',
      'Alice Kavanagh',
      avatar(9),
      5,
      'Our new Friday place',
      'Sat at the counter, watched them work, ate too much. The staff clearly like being there and it shows in the room.',
      '2026-07-05'
    ),
    review(
      'demo-yelp-2',
      'yelp',
      'Jonas Weber',
      avatar(10),
      5,
      'Worth the wait',
      'Twenty minutes on a Saturday night, which for this part of town is nothing. Would have waited longer.',
      '2026-06-22'
    ),
    review(
      'demo-yelp-3',
      'yelp',
      'Marta Silva',
      avatar(11),
      4,
      'Great food, loud room',
      'No complaints about anything on the plate. Bring someone you do not need to have a quiet conversation with.',
      '2026-06-08'
    ),
    review(
      'demo-yelp-4',
      'yelp',
      'Oliver Grant',
      avatar(12),
      5,
      'They remembered us',
      'Second visit and they remembered what we ordered the first time. Small thing, but that is why we keep going back.',
      '2026-05-19'
    ),
    review(
      'demo-yelp-5',
      'yelp',
      'Diego Herrera',
      avatar(17),
      5,
      'Best plate I have had this year',
      'Went for the special on a whim and have thought about it since.',
      '2026-05-11'
    ),
    review(
      'demo-yelp-6',
      'yelp',
      'Bea Whitmore',
      avatar(18),
      5,
      'They looked after us properly',
      'We came in with a toddler ten minutes before the kitchen closed and were treated like the first table of the night.',
      '2026-04-27'
    ),
    review(
      'demo-yelp-7',
      'yelp',
      'Hugo Lambert',
      avatar(19),
      4,
      'Excellent food, tight tables',
      'You will hear your neighbours plans for the weekend. Worth it.',
      '2026-04-13'
    ),
    review(
      'demo-yelp-8',
      'yelp',
      'Nadia Karim',
      avatar(20),
      5,
      'The wine list is doing real work',
      'Asked for something unusual under thirty and got exactly that, with an explanation.',
      '2026-03-30'
    ),
  ],

  tripadvisor: [
    review(
      'demo-tripadvisor-1',
      'tripadvisor',
      'Rachel Whitfield',
      avatar(13),
      5,
      'Better than the photos',
      'Room at the back overlooking the courtyard, quiet as anything despite being two minutes from the station. Breakfast was a proper breakfast.',
      '2026-07-11',
      'Thank you Rachel — the courtyard rooms are our favourites too.'
    ),
    review(
      'demo-tripadvisor-2',
      'tripadvisor',
      'Yusuf Demir',
      avatar(14),
      5,
      'The staff made the trip',
      'Reception rearranged a tour for us when the weather turned and did not make it feel like a favour.',
      '2026-06-29'
    ),
    review(
      'demo-tripadvisor-3',
      'tripadvisor',
      'Clara Nguyen',
      avatar(15),
      4,
      'Lovely stay, thin walls',
      'Everything else was excellent. Ask for a room away from the stairwell and you will have no complaints at all.',
      '2026-06-14'
    ),
    review(
      'demo-tripadvisor-4',
      'tripadvisor',
      'Ian Sutcliffe',
      avatar(16),
      5,
      'Third year running',
      'We stop here every year on the way south and it has never once been a disappointment.',
      '2026-05-26'
    ),
    review(
      'demo-tripadvisor-5',
      'tripadvisor',
      'Margaret Cole',
      avatar(1),
      5,
      'Worth the detour',
      'We were passing and booked one night. We stayed three.',
      '2026-05-09'
    ),
    review(
      'demo-tripadvisor-6',
      'tripadvisor',
      'Stefan Bauer',
      avatar(2),
      5,
      'Breakfast is not an afterthought',
      'Proper bread, eggs cooked to order, and nobody rushing you out at nine.',
      '2026-04-25'
    ),
    review(
      'demo-tripadvisor-7',
      'tripadvisor',
      'Aoife Byrne',
      avatar(3),
      4,
      'Beautiful, slightly dated bathroom',
      'The room itself is lovely and the rest of the hotel is immaculate. The shower is of its era.',
      '2026-04-08'
    ),
    review(
      'demo-tripadvisor-8',
      'tripadvisor',
      'Kwame Asante',
      avatar(4),
      5,
      'They remembered the anniversary',
      'I mentioned it once when booking. There was a card on the bed.',
      '2026-03-22'
    ),
  ],

  airbnb: [
    review(
      'demo-airbnb-1',
      'airbnb',
      'Lena Fischer',
      avatar(17),
      5,
      'Spotless, and the guide was gold',
      'The little notebook of local places was better than anything I found online. Used it every day.',
      '2026-07-07'
    ),
    review(
      'demo-airbnb-2',
      'airbnb',
      'Sofia Marchetti',
      avatar(18),
      5,
      'Exactly as described',
      'Which sounds like faint praise and is not — every photo was the real room, at the real size.',
      '2026-06-24'
    ),
    review(
      'demo-airbnb-3',
      'airbnb',
      'Ben Okafor',
      avatar(19),
      5,
      'Easy from start to finish',
      'Check-in instructions arrived before I thought to ask for them. Left the keys and went, no fuss.',
      '2026-06-02'
    ),
    review(
      'demo-airbnb-4',
      'airbnb',
      'Nina Halvorsen',
      avatar(20),
      4,
      'Great spot, steep stairs',
      'Worth knowing if you are travelling with a heavy case. The view at the top makes up for it.',
      '2026-05-15'
    ),
    review(
      'demo-airbnb-5',
      'airbnb',
      'Julia Sandberg',
      avatar(5),
      5,
      'Better equipped than my own kitchen',
      'We cooked every night rather than eating out, which was half the point of the trip.',
      '2026-05-06'
    ),
    review(
      'demo-airbnb-6',
      'airbnb',
      'Andre Costa',
      avatar(6),
      5,
      'Quiet, which is rare here',
      'Right in the middle of everything and we did not hear a thing after ten.',
      '2026-04-21'
    ),
    review(
      'demo-airbnb-7',
      'airbnb',
      'Hana Suzuki',
      avatar(7),
      4,
      'Lovely, small parking note',
      'The space is exactly as described. Parking is street-only, so arrive before six if you can.',
      '2026-04-05'
    ),
    review(
      'demo-airbnb-8',
      'airbnb',
      'Liam O’Sullivan',
      avatar(8),
      5,
      'Host answered in minutes',
      'We locked ourselves out on the first evening. Sorted before we had finished panicking.',
      '2026-03-18'
    ),
  ],
  /*
    The four the Chrome extension collects.

    Six each rather than eight: these appear in the source pages' selection step
    and in the builder behind them, and neither asks for more. They exist at all
    because the alternative was worse — an Etsy page telling somebody "these are
    Etsy sample reviews" while showing Google-marked reviews of a plumber, which
    is a small lie on a page whose entire job is explaining a mechanism
    accurately.
  */
  googleplay: [
    review(
      'demo-googleplay-1',
      'googleplay',
      'Kwame Asante',
      avatar(2),
      5,
      '',
      'Syncs between my phone and laptop instantly. Finally stopped emailing myself notes.',
      '2026-07-14',
      'Thanks Kwame — glad the sync is earning its keep!'
    ),
    review(
      'demo-googleplay-2',
      'googleplay',
      'Lucia Marin',
      avatar(9),
      5,
      '',
      'Clean, fast, no clutter. It does one thing and does it really well.',
      '2026-07-02'
    ),
    review(
      'demo-googleplay-3',
      'googleplay',
      'Jonas Weber',
      avatar(11),
      4,
      '',
      'Great app. Widgets on the home screen would make it perfect.',
      '2026-06-20',
      'Widgets are in the next release — keep an eye out.'
    ),
    review(
      'demo-googleplay-4',
      'googleplay',
      'Meera Joshi',
      avatar(12),
      5,
      '',
      'Works offline on the train, which is exactly when I need it most.',
      '2026-06-08'
    ),
    review(
      'demo-googleplay-5',
      'googleplay',
      'Paulo Almeida',
      avatar(17),
      5,
      '',
      'Switched from a paid alternative and haven’t looked back. The free tier is honestly generous.',
      '2026-05-26'
    ),
    review(
      'demo-googleplay-6',
      'googleplay',
      'Hannah Meyer',
      avatar(18),
      4,
      '',
      'Battery use is much better since the last update. Dark mode is lovely.',
      '2026-05-11'
    ),
  ],

  /* The App Store shows no reviewer photos, and nicknames are the norm there. */
  appstore: [
    review(
      'demo-appstore-1',
      'appstore',
      'nightowl_dev',
      null,
      5,
      'Replaced three apps',
      'I used to juggle a to-do app, a notes app and a calendar. Now it is all here and it actually talks to itself.',
      '2026-07-11'
    ),
    review(
      'demo-appstore-2',
      'appstore',
      'Mara from Leeds',
      null,
      5,
      'Beautifully made',
      'Feels like it belongs on the iPhone. Every animation is quick and nothing gets in the way.',
      '2026-06-29'
    ),
    review(
      'demo-appstore-3',
      'appstore',
      'jt.kowal',
      null,
      4,
      'Almost perfect',
      'Love it on iPhone and iPad. An Apple Watch app would complete it.',
      '2026-06-15'
    ),
    review(
      'demo-appstore-4',
      'appstore',
      'ChloeReads',
      null,
      5,
      'Worth the subscription',
      'I was sceptical about paying monthly, but it saves me an hour a week easily.',
      '2026-06-01'
    ),
    review(
      'demo-appstore-5',
      'appstore',
      'Ben_Okafor',
      null,
      5,
      'Support is brilliant',
      'Lost my data after switching phones and had it restored within the hour. Proper humans answering.',
      '2026-05-19'
    ),
    review(
      'demo-appstore-6',
      'appstore',
      'quietcoffee',
      null,
      4,
      'Simple and fast',
      'Opens instantly and gets out of the way. A widget for the lock screen would be nice.',
      '2026-05-04'
    ),
  ],

  etsy: [
    review(
      'demo-etsy-1',
      'etsy',
      'Rowan Hale',
      avatar(1),
      5,
      'Even lovelier in person',
      'The photographs do not do the glaze justice. Wrapped beautifully too — it did not need re-wrapping to give as a gift.',
      '2026-06-30'
    ),
    review(
      'demo-etsy-2',
      'etsy',
      'Meera Joshi',
      avatar(2),
      5,
      'Custom order, no fuss',
      'Asked for a different length and got a reply the same evening. Arrived exactly as we agreed.',
      '2026-06-12'
    ),
    review(
      'demo-etsy-3',
      'etsy',
      'Ellis Warren',
      avatar(3),
      4,
      'Beautiful, slow post',
      'No complaints about the piece at all. It sat with the courier for a week, which is nobody’s fault but worth knowing.',
      '2026-05-28'
    ),
    review(
      'demo-etsy-4',
      'etsy',
      'Camille Roux',
      avatar(4),
      5,
      'Third order this year',
      'I keep buying these as presents and keep being asked where they came from.',
      '2026-05-14'
    ),
    review(
      'demo-etsy-5',
      'etsy',
      'Jonah Pike',
      avatar(5),
      5,
      'Packed like it mattered',
      'Tissue, a handwritten note, the lot. It is a small thing and it is why I came back.',
      '2026-04-30'
    ),
    review(
      'demo-etsy-6',
      'etsy',
      'Anya Sokolova',
      avatar(6),
      5,
      'Exactly the colour I asked about',
      'Messaged first because screens lie about colour. She sent a photo in daylight. Spot on.',
      '2026-04-16'
    ),
  ],

  amazon: [
    review(
      'demo-amazon-1',
      'amazon',
      'Derek Simmons',
      avatar(7),
      5,
      'Replaced one that lasted two years',
      'Same job, noticeably better build. The old one rattled from month three; this does not.',
      '2026-06-25'
    ),
    review(
      'demo-amazon-2',
      'amazon',
      'Fatima Zahra',
      avatar(8),
      5,
      'Assembly took ten minutes',
      'Instructions were actually written by somebody who had built one.',
      '2026-06-09'
    ),
    review(
      'demo-amazon-3',
      'amazon',
      'Neil Armitage',
      avatar(9),
      4,
      'Works well, runs warm',
      'Does everything it claims. Gets warmer than I expected under load, which has not caused a problem.',
      '2026-05-22'
    ),
    review(
      'demo-amazon-4',
      'amazon',
      'Lucia Marin',
      avatar(10),
      5,
      'Bought a second one',
      'One for the office and one for home. That is the review, really.',
      '2026-05-06'
    ),
    review(
      'demo-amazon-5',
      'amazon',
      'Owen Tanaka',
      avatar(11),
      5,
      'Support answered on a Sunday',
      'A part was missing. Replacement arrived Tuesday with an apology I did not need but appreciated.',
      '2026-04-20'
    ),
    review(
      'demo-amazon-6',
      'amazon',
      'Bridget Kelly',
      avatar(12),
      4,
      'Good, box was battered',
      'The product was fine. The packaging had clearly had an adventure.',
      '2026-04-03'
    ),
  ],

  aliexpress: [
    review(
      'demo-aliexpress-1',
      'aliexpress',
      'Sasha Ivanov',
      avatar(13),
      5,
      'Faster than the estimate',
      'Said four weeks, arrived in eleven days. Well packed, nothing bent.',
      '2026-06-21'
    ),
    review(
      'demo-aliexpress-2',
      'aliexpress',
      'Paulo Almeida',
      avatar(14),
      5,
      'Matches the listing photos',
      'Which is not always true here. This one does.',
      '2026-06-05'
    ),
    review(
      'demo-aliexpress-3',
      'aliexpress',
      'Ingrid Møller',
      avatar(15),
      4,
      'Good value, thin manual',
      'The product is genuinely good for the money. The instructions are one folded sheet.',
      '2026-05-19'
    ),
    review(
      'demo-aliexpress-4',
      'aliexpress',
      'Chen Wei',
      avatar(16),
      5,
      'Ordered five, all identical',
      'Consistency is the thing I was worried about. No complaints.',
      '2026-05-02'
    ),
    review(
      'demo-aliexpress-5',
      'aliexpress',
      'Marta Nowicka',
      avatar(17),
      5,
      'Seller answered every question',
      'I asked three fairly pedantic things before ordering and got proper answers to all of them.',
      '2026-04-15'
    ),
    review(
      'demo-aliexpress-6',
      'aliexpress',
      'Tunde Adeyemi',
      avatar(18),
      4,
      'Solid, one scuff',
      'A mark on the underside where nobody will see it. Not worth returning.',
      '2026-03-28'
    ),
  ],

  tourradar: [
    review(
      'demo-tourradar-1',
      'tourradar',
      'Helen Prescott',
      avatar(19),
      5,
      'The guide made the trip',
      'Twelve days and he never once ran out of things worth telling us. Knew exactly when to leave us alone, too.',
      '2026-06-18'
    ),
    review(
      'demo-tourradar-2',
      'tourradar',
      'Mateo Rivas',
      avatar(20),
      5,
      'Well paced, not a route march',
      'Enough built-in time to actually look at things rather than photograph them from a moving bus.',
      '2026-06-01'
    ),
    review(
      'demo-tourradar-3',
      'tourradar',
      'Saskia Vermeer',
      avatar(1),
      4,
      'Excellent, early starts',
      'No complaints about the itinerary. Several six a.m. departures, which is worth knowing before you book.',
      '2026-05-16'
    ),
    review(
      'demo-tourradar-4',
      'tourradar',
      'George Mbeki',
      avatar(2),
      5,
      'Small group, properly small',
      'Eight of us. It said up to twelve and I half expected sixteen.',
      '2026-04-29'
    ),
    review(
      'demo-tourradar-5',
      'tourradar',
      'Ines Ferreira',
      avatar(3),
      5,
      'They sorted a missed connection',
      'My flight was delayed and somebody was still waiting at eleven at night. That is the whole review.',
      '2026-04-12'
    ),
    review(
      'demo-tourradar-6',
      'tourradar',
      'Karl Jensen',
      avatar(4),
      5,
      'Booked the next one already',
      'Different continent, same operator. That is the strongest thing I can say.',
      '2026-03-25'
    ),
  ],
};
/**
 * ===========================================================================
 * Photographs, on some of them
 * ===========================================================================
 * Masonry is the layout whose whole reason to exist is that review cards are
 * not the same height — it packs uneven cards without leaving gaps. Every demo
 * review above was written with `pictures: []`, so every card came out within a
 * line or two of the same height and the wall rendered as a slightly ragged
 * grid. The layout was working; it had nothing to work *on*, so the one thing
 * it does better than `grid` never showed up in any preview of it.
 *
 * So photos are attached here rather than typed into all seventy-two entries
 * above. Doing it in one place also keeps the decision visible: it is *which
 * reviews have photos* that makes masonry look like masonry, and that is a
 * property of the set, not of any single review.
 *
 * Two rules, both load-bearing:
 *
 *   Not all of them. A photo on every card is uniform again, just taller — the
 *   heights have to actually differ. Roughly every other one, by index, so the
 *   pattern is deterministic and the same across a server render and a client
 *   one. `Math.random()` here would produce a hydration mismatch.
 *
 *   Photos that suit the business. The pool is per source, so the hotel gets a
 *   room's worth of things and the marketplaces get products. These are the
 *   same drawn SVGs `SAMPLE_REVIEWS` uses and this site already serves; a photo
 *   of a mug on a Tripadvisor review of a hotel would be the kind of detail that
 *   makes a demo feel assembled rather than made.
 */
/**
 * ---------------------------------------------------------------------------
 * The subject matters more than the shape
 * ---------------------------------------------------------------------------
 * That sentence is the other way round from how this note used to read, and
 * the reversal is the point — see the pool below for what the old priority
 * produced.
 *
 * The six files are photographs rather than the drawn product SVGs in
 * `/images/sample`, and that part was right: every one of those is 752×564, so
 * identical aspect ratios gave identical photo heights and a masonry wall of
 * them came out as a grid with pictures in it.
 *
 *   room, dish          522×700  portrait
 *   breakfast           700×700  square
 *   boiler              700×522  landscape
 *   courtyard, parcel   700×390  wide
 *
 * The ratios are still worth having where a set can honestly use several. They
 * are not worth an untrue picture: a demo is convincing when the photograph
 * matches the sentence beside it, and unconvincing the instant it does not.
 */
/*
  ---------------------------------------------------------------------------
  A photo has to be one the reviewer could have taken
  ---------------------------------------------------------------------------
  These pools were assembled for *shape* — three or four ratios each, so a
  masonry wall had something uneven to pack — and the subjects were treated as
  interchangeable filler in service of that. They are not interchangeable, and
  the failure was visible the moment somebody picked a business type in the free
  builder: the Trustpilot set is software copy ("a billing question at eight in
  the evening", "the reporting screens", "migration was painless") and it was
  illustrated with a photograph of a hotel room, a cooked breakfast and a
  boiler in a cupboard.

  That is worse than an odd-looking wall. Every one of these cards is a claim
  about what a customer's own widget will look like, and a review of accounting
  software with a picture of a bedroom on it tells a visitor that the pictures
  are decoration we chose — which invites them to wonder what else is.

  So each pool is now only the photographs a reviewer of *that* business would
  plausibly have taken. Where the honest answer is fewer photos, it is fewer;
  where it is none, the source is absent from this table and `withPhotos`
  leaves the set alone.

  ---------------------------------------------------------------------------
  Software has no pool, and that is the point
  ---------------------------------------------------------------------------
  Nobody photographs their SaaS. Reviews with pictures on them are a hospitality
  and marketplace phenomenon — a room, a plate, a parcel, a finished job — and
  a software review that arrived with a snapshot would be the odd one. Leaving
  `trustpilot` out is the accurate demonstration, not a gap to be filled the
  next time somebody adds a stock photograph to `public/`.

  The layout still varies, because the variation that matters is photo against
  no photo — a card with a picture is much taller than one without, whatever
  the picture's ratio. The ratio spread is a bonus the hospitality sets happen
  to keep; it was never worth an untrue image to buy.
*/
const PHOTO_POOL: Partial<Record<ReviewSourceId, string[]>> = {
  /* Trades: "sorted in one visit", "explained it properly". A finished job. */
  google: ['boiler'],
  /*
    No `googleplay` or `appstore` either: nobody photographs an app, and the
    App Store has no photos to import. Deliberately no `trustpilot`. See above — that set is software, and none of
    the six photographs is one of its reviewers could have taken.
  */
  /* A restaurant: the plate, the table outside, what breakfast looked like. */
  yelp: ['dish', 'courtyard', 'breakfast'],
  /* A hotel: the room, the breakfast, the courtyard, dinner. All four fit. */
  tripadvisor: ['room', 'breakfast', 'courtyard', 'dish'],
  /* A holiday let: the room and the outside space. Not the restaurant's dish. */
  airbnb: ['room', 'courtyard', 'breakfast'],
  /*
    The three marketplaces all get the parcel and nothing else. "Packed like it
    mattered", "box was battered", "matches the listing photos" — what a buyer
    photographs is what turned up, and the one photograph of that is `parcel`.
  */
  etsy: ['parcel'],
  amazon: ['parcel'],
  aliexpress: ['parcel'],
  /* A tour: where they were taken. Not the hotel bedroom. */
  tourradar: ['courtyard'],
};

const photo = (name: string) => `/images/review-photos/${name}.jpg`;

function withPhotos(reviews: PreviewReview[]): PreviewReview[] {
  return reviews.map((entry, index) => {
    const pool = PHOTO_POOL[entry.source];
    // Every other card, starting with the first, so a wall of six has three
    // tall cards and three short ones rather than one odd one out.
    if (!pool || index % 2 !== 0) return entry;
    return { ...entry, pictures: [photo(pool[(index / 2) % pool.length]!)] };
  });
}

for (const [source, reviews] of Object.entries(DEMO_REVIEWS)) {
  DEMO_REVIEWS[source] = withPhotos(reviews);
}

/**
 * Every demo review, in source order — the "one library" view.
 *
 * Used where a widget is being shown for its own sake rather than for a
 * platform's: the widget gallery, the free builder's default, the homepage's
 * finished-website example.
 */
export const ALL_DEMO_REVIEWS: PreviewReview[] = Object.values(DEMO_REVIEWS).flat();

export function demoReviewsFor(source: ReviewSourceId): PreviewReview[] {
  return DEMO_REVIEWS[source] ?? [];
}
